import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

function proj(x: number, y: number, z: number, ox = 0, oy = 0): [number, number] {
  return [ox + x - y * 0.4, oy - z + y * 0.35];
}
function faceStr(corners: [number, number][], indices: number[]) {
  return indices.map(i => `${corners[i][0].toFixed(1)},${corners[i][1].toFixed(1)}`).join(' ');
}
function boxCorners(sx: number, sy: number, sz: number, ox = 0, oy = 0) {
  return [
    proj(0,  0,  0,  ox, oy),
    proj(sx, 0,  0,  ox, oy),
    proj(sx, sy, 0,  ox, oy),
    proj(0,  sy, 0,  ox, oy),
    proj(0,  0,  sz, ox, oy),
    proj(sx, 0,  sz, ox, oy),
    proj(sx, sy, sz, ox, oy),
    proj(0,  sy, sz, ox, oy),
  ];
}

export function ScaleDiagram({ values }: DiagramProps) {
  const v = values.v ?? [2, 2, 2];
  const vx = Math.max(0.1, v[0] ?? 2);
  const vy = Math.max(0.1, v[1] ?? 2);
  const vz = Math.max(0.1, v[2] ?? 2);

  const base = 20; // base box size in pixels
  const maxScale = Math.max(vx, vy, vz, 1);
  const normalizer = 70 / (base * maxScale);

  const srcS = base * normalizer;
  const dstSx = base * vx * normalizer;
  const dstSy = base * vy * normalizer;
  const dstSz = base * vz * normalizer;

  const ox = 70, oy = 105;
  const srcC = boxCorners(srcS, srcS, srcS, ox, oy);
  const dstC = boxCorners(dstSx, dstSy, dstSz, ox, oy);

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Scale diagram">
      <defs>
        <marker id="arr-sc-x" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.X_COLOR} />
        </marker>
        <marker id="arr-sc-y" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.Y_COLOR} />
        </marker>
        <marker id="arr-sc-z" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.Z_COLOR} />
        </marker>
      </defs>

      {/* Ghost original box */}
      <polygon points={faceStr(srcC, [0,1,5,4])}
               fill="none" stroke={D.GEO_STROKE_GHOST} strokeWidth="1" strokeDasharray="4 3" />
      <polygon points={faceStr(srcC, [1,2,6,5])}
               fill="none" stroke={D.GEO_STROKE_GHOST} strokeWidth="1" strokeDasharray="4 3" />
      <polygon points={faceStr(srcC, [4,5,6,7])}
               fill="none" stroke={D.GEO_STROKE_GHOST} strokeWidth="1" strokeDasharray="4 3" />

      {/* Scaled box */}
      <polygon points={faceStr(dstC, [0,1,2,3])}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
      <polygon points={faceStr(dstC, [0,1,5,4])}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <polygon points={faceStr(dstC, [1,2,6,5])}
               fill="rgba(96,165,250,0.04)" stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <polygon points={faceStr(dstC, [4,5,6,7])}
               fill="rgba(96,165,250,0.10)" stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* X scale arrow */}
      <line x1={dstC[0][0]} y1={dstC[0][1] + 10}
            x2={dstC[1][0]} y2={dstC[1][1] + 10}
            stroke={D.X_COLOR} strokeWidth="1.2" markerEnd="url(#arr-sc-x)" />
      <text x={(dstC[0][0] + dstC[1][0]) / 2} y={dstC[0][1] + 22}
            fontSize="10" fontFamily="monospace" fill={D.X_COLOR} textAnchor="middle" opacity="0.9">
        ×{vx}
      </text>

      {/* Y scale arrow */}
      <line x1={dstC[1][0] + 6} y1={dstC[1][1]}
            x2={dstC[2][0] + 6} y2={dstC[2][1]}
            stroke={D.Y_COLOR} strokeWidth="1.2" markerEnd="url(#arr-sc-y)" />
      <text x={dstC[2][0] + 14} y={(dstC[1][1] + dstC[2][1]) / 2 + 4}
            fontSize="10" fontFamily="monospace" fill={D.Y_COLOR} textAnchor="start" opacity="0.9">
        ×{vy}
      </text>

      {/* Z scale arrow */}
      <line x1={dstC[1][0] + 14} y1={dstC[1][1]}
            x2={dstC[5][0] + 14} y2={dstC[5][1]}
            stroke={D.Z_COLOR} strokeWidth="1.2" markerEnd="url(#arr-sc-z)" />
      <text x={dstC[5][0] + 18} y={(dstC[1][1] + dstC[5][1]) / 2 + 4}
            fontSize="10" fontFamily="monospace" fill={D.Z_COLOR} textAnchor="start" opacity="0.9">
        ×{vz}
      </text>

      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45">v = [scaleX, scaleY, scaleZ]</text>
    </svg>
  );
}
